// 성장 v1(F3 확장): 레벨·EXP·스킬 포인트·스킬 트리의 단일 데이터와 순수 계산. 게임·UI·렌더가 함께 읽는다. three import 금지.
// 정본: docs/SKILL-TREE.md. 수치를 바꾸면 그 문서를 먼저 고친다.
// 프레임 루프에서 부르는 함수(growthMods 등)는 새 객체를 만들지 않는다(호출자가 out을 재사용).
import { SKILL_AUTO_LEARN, type SkillHeroId } from './skills/types';
import { skillBit, skillsUnlockedAt } from './skills/unlock';
import { XP_TO_NEXT, XP_TO_NEXT_V2 } from './xpTable';

// ── 레벨·EXP ─────────────────────────────────────

/** 2026-10-21 개정: 1~99(CONTENT-90D-PRD 3A.4). 필요 EXP는 시뮬이 만든 표(xpTable.ts)를 쓴다 */
export const LEVEL_MAX = 99;
/** 레벨 L → L+1에 필요한 EXP(xpTable.ts). 최고 레벨이면 0 */
export function xpToNext(level: number): number {
  return level >= LEVEL_MAX || level < 1 ? 0 : XP_TO_NEXT[level];
}
function cumulative(maxLevel: number, step: (l: number) => number): number[] {
  const out = [0, 0];
  for (let l = 1; l < maxLevel; l++) out.push(out[l] + step(l));
  return out;
}
/** 레벨 L에 도달하는 누적 EXP(L=1 → 0) */
export const XP_AT_LEVEL: readonly number[] = cumulative(LEVEL_MAX, xpToNext);
export const XP_MAX = XP_AT_LEVEL[LEVEL_MAX];
/** 누적 EXP → 레벨(1..99) */
export function levelFromXp(xp: number): number {
  return levelIn(XP_AT_LEVEL, LEVEL_MAX, xp);
}
function levelIn(table: readonly number[], max: number, xp: number): number {
  let lv = 1;
  while (lv < max && xp >= table[lv + 1]) lv++;
  return lv;
}

/**
 * 2026-10-30 이전 곡선(하루 1.25h·Lv99≈110h, xpTable.XP_TO_NEXT_V2). 720h 곡선(docs/PROGRESSION-720H.md 2절)은 Lv1~54가 같고 Lv55부터 길다.
 * 이전 곡선으로 쌓은 EXP를 같은 레벨·같은 구간 진행률로 옮기는 curve-v3 이관(game/growth.ts)에서만 쓴다.
 */
export const XP_AT_LEVEL_V2: readonly number[] = cumulative(LEVEL_MAX, (l) => XP_TO_NEXT_V2[l]);
/** 두 곡선이 갈라지는 누적 EXP: 이 값 이하(Lv1~54 구간과 Lv55 시작점)는 두 곡선에서 같은 레벨·같은 진행률이다 */
export const CURVE_SPLIT_XP: number = (() => {
  let l = 1;
  while (l < LEVEL_MAX && XP_TO_NEXT[l] === XP_TO_NEXT_V2[l]) l++;
  return XP_AT_LEVEL[l];
})();
/** 이전 곡선 누적 EXP → 720h 곡선 누적 EXP(레벨·구간 진행률 보존, Lv99 위로 남은 EXP는 그대로 더한다). 새 곡선이 모든 레벨에서 같거나 커서 결과 ≥ 입력 */
export function migrateXpV2(xpV2: number): number {
  const x = Math.max(0, Math.floor(xpV2));
  if (x >= XP_AT_LEVEL_V2[LEVEL_MAX]) return XP_AT_LEVEL[LEVEL_MAX] + (x - XP_AT_LEVEL_V2[LEVEL_MAX]);
  const lv = levelIn(XP_AT_LEVEL_V2, LEVEL_MAX, x);
  const span = XP_AT_LEVEL_V2[lv + 1] - XP_AT_LEVEL_V2[lv];
  const frac = span > 0 ? (x - XP_AT_LEVEL_V2[lv]) / span : 0;
  return XP_AT_LEVEL[lv] + Math.floor(frac * (XP_AT_LEVEL[lv + 1] - XP_AT_LEVEL[lv]));
}

/** 이전 곡선(성장 v1, 1~30, `60·L^1.5+100`). growth.v1 → v2 이관에서만 쓴다(SKILL-TREE 6절) */
export const LEVEL_MAX_V1 = 30;
export const XP_AT_LEVEL_V1: readonly number[] = cumulative(LEVEL_MAX_V1, (l) => Math.round(60 * Math.pow(l, 1.5) + 100));
export const XP_MAX_V1 = XP_AT_LEVEL_V1[LEVEL_MAX_V1];
/** 이전 곡선의 누적 EXP를 같은 레벨·같은 구간 진행률의 새 누적 EXP로 바꾼다(레벨 보존 이관) */
export function migrateXpV1(xpV1: number): number {
  const x = Math.max(0, Math.min(XP_MAX_V1, Math.floor(xpV1)));
  const lv = levelIn(XP_AT_LEVEL_V1, LEVEL_MAX_V1, x);
  if (lv >= LEVEL_MAX_V1) return XP_AT_LEVEL[LEVEL_MAX_V1];
  const span = XP_AT_LEVEL_V1[lv + 1] - XP_AT_LEVEL_V1[lv];
  const frac = span > 0 ? (x - XP_AT_LEVEL_V1[lv]) / span : 0;
  return XP_AT_LEVEL[lv] + Math.floor(frac * (XP_AT_LEVEL[lv + 1] - XP_AT_LEVEL[lv]));
}

/** 레벨당 영웅 기본 능력치 증가(장비·트리와 별개로 더한다) */
export const LEVEL_STATS = { attack: 1, maxHp: 8, maxMp: 3, mpRegen: 0.02 } as const;
/**
 * 스킬 칸이 늘어나는 레벨의 MP 보정(mp-feel-fix 2026-11-02, docs/BALANCE-1028.md 9절).
 * BALANCE-1028은 스킬 5개 회전으로 MP를 맞췄다. 그 뒤 C9(Lv15·20·25 새 스킬, MP 55·60·75)와 전직 I·II(Lv30·40, MP 70~90)가 회전에 들어왔는데
 * MP 공급은 그대로라 Lv35 '가득→바닥' 스킬 수가 7→5, Lv65 10→7로 줄었다(대표님 "엠피가 너무 모자르다, 전이 나았어").
 * 그 레벨에 닿으면 최대 MP와 초당 회복 배율을 한 번씩 더한다. 레벨 기준이라 전직 전이어도 받는다(스킬 해금 레벨과 같은 자리).
 */
export const MP_SKILL_STEPS: readonly { readonly level: number; readonly maxMp: number; readonly mpRegen: number }[] = [
  { level: 15, maxMp: 20, mpRegen: 0.1 }, { level: 20, maxMp: 20, mpRegen: 0.1 }, { level: 25, maxMp: 25, mpRegen: 0.1 },
  { level: 30, maxMp: 30, mpRegen: 0.15 }, { level: 40, maxMp: 30, mpRegen: 0.15 },
];
/**
 * 레벨 L에서 가진 총 스킬 포인트: 레벨업마다 1, 10레벨마다 1 더(Lv30 = 32로 이전과 같다).
 * 지금 트리(15노드)가 받을 수 있는 양(TREE_POINT_CAPACITY, 아래)에서 멈춘다 — Lv42부터 공식 값은 쓸 곳 없는 포인트가 된다.
 * 트리가 3가지 × 10층으로 늘어나면(CONTENT-90D D1e) 상한이 함께 커져 다시 오른다(결정 2026-10-22 c010, docs/SKILL-TREE.md 2절).
 */
export function pointsAtLevel(level: number): number {
  const l = Math.max(1, Math.min(LEVEL_MAX, Math.floor(level)));
  return Math.min((l - 1) + Math.floor(l / 10), TREE_POINT_CAPACITY);
}

/** 처치 EXP(몹 종류 MobKind 순서: 고블린·방패 고블린·멧돼지·주술사). 엘리트 ×3, 지역 배율 2.2^(지역−1) */
export const XP_KILL: readonly number[] = [5, 7, 9, 8];
export const XP_ELITE_MULT = 3;
/** 지역5·6은 720h 데이터 계약(docs/PROGRESSION-720H.md 5절): 같은 규칙 2.2^(지역−1)로 잇는다. 지도는 C2가 만든다 */
export const XP_REGION_MULT = { r1: 1, r2: 2.2, r3: 4.84, r4: 10.648, r5: 23.4256, r6: 51.53632 } as const;
/** 보스 처치·지역 클리어 EXP(지역 배율을 따른다) */
export const XP_BOSS = { r1: 250, r2: 550, r3: 1210, r4: 2662, r5: 5856, r6: 12884 } as const;
export const XP_CLEAR = { r1: 200, r2: 440, r3: 968, r4: 2130, r5: 4685, r6: 10307 } as const;
/** 패배해도 그 판에서 얻은 EXP는 남는다(벌칙 없음) */
export const XP_KEEP_ON_DEFEAT = 1;
/** 처치 EXP(몹 종류·엘리트·지역). 반올림해 정수 EXP만 쓴다 */
/** 스테이지 배율 계단(SKILL-TREE 1절): 스테이지 s의 몹 HP·피해·처치 EXP 배율 = 1 + 0.12×(s−1). content/world.ts STAGE_STEP의 정본 */
export const STAGE_STEP = 0.12;
/** 스테이지 s(1..10)의 처치 EXP 배율. 몹 HP 배율(StageDef.hpMul)과 같다 */
export function stageXpMult(stage: number): number {
  return 1 + STAGE_STEP * (Math.max(1, stage) - 1);
}
/**
 * 처치 EXP = 종류 기본 × 엘리트 × 지역 배율 × 스테이지 배율(SKILL-TREE 1절).
 * 지금 게임 맵(r1·r2)은 각 지역 1스테이지라 stage 기본값 1(×1). 스테이지 시스템(D3a)이 들어오면 호출부가 스테이지 번호를 넘긴다.
 */
export function killXp(kind: number, elite: boolean, region: keyof typeof XP_REGION_MULT, stage = 1): number {
  const base = XP_KILL[kind] ?? 0;
  return Math.round(base * (elite ? XP_ELITE_MULT : 1) * XP_REGION_MULT[region] * stageXpMult(stage));
}
/** 지역 몹 레벨 = 그 지역 권장 구간의 끝(CONTENT-90D world.ts levels[1]) */
export const REGION_MOB_LEVEL = { r1: 18, r2: 30, r3: 40, r4: 55, r5: 68, r6: 80 } as const;
/** 레벨 차 보정(SKILL-TREE 1절): 몹보다 5↑ ×0.5, 10↑ ×0.1 */
export const XP_LEVEL_DIFF = { half: 5, tenth: 10 } as const;
export function levelDiffXpMult(heroLevel: number, mobLevel: number): number {
  const d = heroLevel - mobLevel;
  return d >= XP_LEVEL_DIFF.tenth ? 0.1 : d >= XP_LEVEL_DIFF.half ? 0.5 : 1;
}
/**
 * 고레벨 EXP 배율(C10-3, docs/PROGRESSION-720H.md 14절): 720h 곡선(xpTable)은 분당 처치 30 가정으로 맞췄는데 실입력 표본(C3)은
 * 분당 53(봇 89 × 사람 0.6)이라 Lv99가 약 124h에 닿아 첫 달 콘텐츠가 비었다. 표(레벨 요구 EXP)를 바꾸면 저장 이관(curve-v4)이 필요하므로
 * 표는 그대로 두고 Lv50→65에서 얻는 EXP를 mult까지 매끄럽게 줄인다(경계 완만화, PR #317 리뷰) — 레벨은 누적 EXP로 정해지므로 이미 쌓은 레벨·EXP는 그대로다(내려가지 않는다).
 */
export const XP_BAND = { from: 50, full: 65, mult: 0.52 } as const;
export function levelBandXpMult(heroLevel: number): number {
  if (!(heroLevel > XP_BAND.from)) return 1; // NaN·Lv50 이하 = 1(저장 잠금 재보정 경로가 NaN을 넘겨도 EXP를 오염시키지 않게)
  if (heroLevel >= XP_BAND.full) return XP_BAND.mult;
  return 1 + (XP_BAND.mult - 1) * (heroLevel - XP_BAND.from) / (XP_BAND.full - XP_BAND.from);
}
/** 보정 후 EXP(양수면 최소 1): 레벨 차 보정 × 고레벨 배율 */
export function adjustedXp(amount: number, heroLevel: number, mobLevel: number): number {
  if (!(amount > 0)) return 0;
  return Math.max(1, Math.round(amount * levelDiffXpMult(heroLevel, mobLevel) * levelBandXpMult(heroLevel)));
}

// ── 스킬 트리 ────────────────────────────────────
export type BranchId = 'crimson' | 'storm' | 'guard';
export interface BranchDef { readonly id: BranchId; readonly name: string; readonly desc: string; readonly color: string }
export const BRANCHES: readonly BranchDef[] = [
  { id: 'crimson', name: '진홍 검술', desc: '강력한 일격과 피의 기운으로 적을 벤다.', color: '#e0313c' },
  { id: 'storm', name: '폭풍 연격', desc: '몰아치는 연속 공격과 연계로 적을 제압한다.', color: '#5aa8ff' },
  { id: 'guard', name: '철벽 수호', desc: '굳건한 몸과 마나로 오래 버티며 내려찍는다.', color: '#e8b04a' },
];
/** 층(아래→위). 층의 노드는 그 레벨부터 배울 수 있다 */
export const TIER_LEVELS: readonly number[] = [1, 5, 10, 15, 20];

export type NodeKind = 'active' | 'passive' | 'capstone';
/**
 * 노드 효과 종류. growthMods가 이 값으로 합친다.
 * per: 랭크당 더하는 값(배율 효과는 +per 비율). unlock: 랭크 5(캡스톤은 1)에 붙는 추가 연출·판정(SkillBurst 종류)
 */
export type NodeEffect =
  | 'skillDamage'      // 이 노드의 skillId 스킬 피해 +per/랭크
  | 'critChance'       // 치명 확률 +per
  | 'skillLifesteal'   // 스킬 처치 시 최대 HP의 per 회복
  | 'basicDamage'      // 기본 공격 피해 +per
  | 'cooldown'         // 모든 스킬 재사용 대기 −per
  | 'linkDamage'       // 연계 2·3단 피해 +per
  | 'maxHp'            // 최대 HP +per
  | 'mana'             // MP 재생 +per, 최대 MP +10/랭크
  | 'damageTaken'      // 받는 피해 −per
  | 'bloom'            // 캡스톤: 연계 3단 피니시에 진홍 개화 폭발
  | 'thunder'          // 캡스톤: 마무리타가 전방으로 폭풍 칼날을 날린다
  | 'resonance';       // 캡스톤: 각성 게이지 획득 +30%, 각성 피해 +25%

export interface SkillNodeDef {
  /** 저장 키(바꾸지 않는다) */
  readonly id: string;
  readonly branch: BranchId;
  /** 0..4 = TIER_LEVELS 인덱스 */
  readonly tier: number;
  readonly kind: NodeKind;
  readonly name: string;
  readonly maxRank: number;
  readonly effect: NodeEffect;
  readonly per: number;
  /** 액티브 노드가 강화하는 스킬(SKILLS id). 패시브는 0 */
  readonly skillId: 0 | 1 | 2 | 3 | 4;
  /** 랭크 5(액티브) 또는 1(캡스톤)에 열리는 추가 효과 이름 */
  readonly bonusName?: string;
  readonly desc: string;
}

/** 순서가 곧 노드 인덱스(저장은 id, 뷰·이벤트는 인덱스). 같은 가지 안에서 tier 오름차순 */
export const SKILL_NODES: readonly SkillNodeDef[] = [
  // 진홍 검술
  { id: 'c_dash', branch: 'crimson', tier: 0, kind: 'active', name: '피의 돌진', maxRank: 5, effect: 'skillDamage', per: 0.12, skillId: 1, bonusName: '핏빛 궤적',
    desc: '돌진 피해 +12%/레벨. 5레벨: 지나간 길이 잠시 뒤 터져 한 번 더 벤다.' },
  { id: 'c_edge', branch: 'crimson', tier: 1, kind: 'passive', name: '날카로운 칼끝', maxRank: 3, effect: 'critChance', per: 0.04, skillId: 0,
    desc: '치명타 확률 +4%/레벨.' },
  { id: 'c_crescent', branch: 'crimson', tier: 2, kind: 'active', name: '진홍 초승달', maxRank: 5, effect: 'skillDamage', per: 0.12, skillId: 4, bonusName: '쌍월',
    desc: 'Lv10에 자동으로 익히는 스킬(L)의 강화. 전방으로 초승달 검기를 날려 길 위의 적을 벤다. 1레벨은 상위 기술로 가는 길이고, 피해 +12%는 2레벨부터. 5레벨: 검기가 두 번 나간다.' },
  { id: 'c_thirst', branch: 'crimson', tier: 3, kind: 'passive', name: '피의 갈망', maxRank: 3, effect: 'skillLifesteal', per: 0.01, skillId: 0,
    desc: '스킬로 적을 쓰러뜨릴 때마다 최대 HP의 1%/레벨 회복.' },
  { id: 'c_bloom', branch: 'crimson', tier: 4, kind: 'capstone', name: '진홍 개화', maxRank: 1, effect: 'bloom', per: 3.0, skillId: 0, bonusName: '진홍 개화',
    desc: '연계 3단 피니시 순간 주변에 진홍 꽃잎 폭발(반경 5m, 위력 300%).' },
  // 폭풍 연격
  { id: 's_storm', branch: 'storm', tier: 0, kind: 'active', name: '칼날 폭풍', maxRank: 5, effect: 'skillDamage', per: 0.10, skillId: 2, bonusName: '폭풍의 눈',
    desc: '폭풍 피해 +10%/레벨. 5레벨: 끌어당김 +3m, 끝에 폭풍의 눈이 터진다(반경 6m, 위력 250%).' },
  { id: 's_flurry', branch: 'storm', tier: 1, kind: 'passive', name: '연격 숙련', maxRank: 3, effect: 'basicDamage', per: 0.06, skillId: 0,
    desc: '기본 공격 피해 +6%/레벨.' },
  { id: 's_gale', branch: 'storm', tier: 2, kind: 'passive', name: '질풍', maxRank: 3, effect: 'cooldown', per: 0.05, skillId: 0,
    desc: '모든 스킬 재사용 대기시간 −5%/레벨.' },
  { id: 's_chain', branch: 'storm', tier: 3, kind: 'passive', name: '연계 증폭', maxRank: 3, effect: 'linkDamage', per: 0.05, skillId: 0,
    desc: '연계 2·3단 스킬 피해 +5%/레벨.' },
  { id: 's_thunder', branch: 'storm', tier: 4, kind: 'capstone', name: '뇌명 검기', maxRank: 1, effect: 'thunder', per: 1.5, skillId: 0, bonusName: '뇌명 검기',
    desc: '기본 공격 4타(마무리타)가 전방 6m로 번개 검기를 날린다(위력 150%).' },
  // 철벽 수호
  { id: 'g_fall', branch: 'guard', tier: 0, kind: 'active', name: '붉은 낙화', maxRank: 5, effect: 'skillDamage', per: 0.12, skillId: 3, bonusName: '여진',
    desc: '낙화 피해 +12%/레벨. 5레벨: 착지 0.4초 뒤 여진이 한 번 더 퍼진다(반경 6m, 위력 80%).' },
  { id: 'g_skin', branch: 'guard', tier: 1, kind: 'passive', name: '강철 피부', maxRank: 3, effect: 'maxHp', per: 0.08, skillId: 0,
    desc: '최대 HP +8%/레벨.' },
  { id: 'g_mana', branch: 'guard', tier: 2, kind: 'passive', name: '마나 순환', maxRank: 3, effect: 'mana', per: 0.15, skillId: 0,
    desc: 'MP 재생 +15%/레벨, 최대 MP +10/레벨.' },
  { id: 'g_iron', branch: 'guard', tier: 3, kind: 'passive', name: '불굴', maxRank: 3, effect: 'damageTaken', per: 0.05, skillId: 0,
    desc: '받는 피해 −5%/레벨.' },
  { id: 'g_resonance', branch: 'guard', tier: 4, kind: 'capstone', name: '각성 공명', maxRank: 1, effect: 'resonance', per: 0.3, skillId: 0, bonusName: '각성 공명',
    desc: '각성 게이지 획득 +30%, 각성 피해 +25%.' },
];
export const NODE_COUNT = SKILL_NODES.length;
const NODE_INDEX = new Map(SKILL_NODES.map((n, i) => [n.id, i]));
export function nodeIndex(id: string): number { return NODE_INDEX.get(id) ?? -1; }

/** 처음부터 1랭크로 가진 노드(기존 세 스킬). 포인트를 쓰지 않으며 초기화해도 1 아래로 내려가지 않는다 */
export const FREE_RANK: readonly number[] = SKILL_NODES.map((n) => (n.kind === 'active' && n.skillId >= 1 && n.skillId <= 3 ? 1 : 0));
/** 트리에 실제로 쓸 수 있는 포인트 총량 = 모든 노드 최대 랭크 합 − 무료 랭크(지금 47 − 3 = 44). pointsAtLevel의 상한 */
export const TREE_POINT_CAPACITY: number = SKILL_NODES.reduce((a, n, i) => a + n.maxRank - FREE_RANK[i], 0);
/** 액티브 노드의 랭크 r(2..5)은 영웅 레벨이 이 값 이상이어야 올릴 수 있다 */
export function activeRankLevel(rank: number): number { return rank <= 1 ? 1 : (rank - 1) * 4; }

/** 진홍 초승달 강화 노드. 스킬 4는 영웅 Lv10에 자동 습득한다(이전에는 이 노드 1랭크가 해금이었다). 노드 랭크 1은 상위 노드 선행 전용 */
export const SKILL4_NODE = nodeIndex('c_crescent');

/** 트리에서 1랭크 이상 배운 액티브 스킬(스킬 번호 비트). 레벨 해금 이전 저장 호환용 */
export function treeSkillMask(ranks: ArrayLike<number>): number {
  let m = 0;
  for (let i = 0; i < NODE_COUNT; i++) {
    const n = SKILL_NODES[i];
    if (n.kind === 'active' && n.skillId >= 1 && (ranks[i] | 0) >= 1) m |= skillBit(n.skillId);
  }
  return m;
}
/** 지금 쓸 수 있는 스킬 비트마스크(비트 i = 스킬 i+1): 무료 3 + 레벨 자동 습득 + 트리 랭크. growthMods의 skillRank ≥ 1과 같은 집합이다 */
export function learnedSkills(level: number, ranks: ArrayLike<number>, hero: SkillHeroId = 'kael', kept = 0): number {
  const lv = Math.max(1, Math.min(LEVEL_MAX, Math.floor(level)));
  // c022: 스킬 트리(SKILL_NODES)는 카엘 전용이다. 다른 영웅은 무료 + 레벨 자동 습득만(랭크 배열은 보지 않는다)
  // kept(레인 R): 환생으로 이어 가는 습득 스킬 비트(레벨 1로 돌아가도 스킬은 남는다)
  return (hero === 'kael' ? skillsUnlockedAt(lv) | treeSkillMask(ranks) : skillsUnlockedAt(lv, hero)) | kept;
}

/** 랭크 배열(길이 NODE_COUNT)에 쓴 포인트(무료 랭크 제외) */
export function spentPoints(ranks: ArrayLike<number>): number {
  let s = 0;
  for (let i = 0; i < NODE_COUNT; i++) s += Math.max(0, (ranks[i] | 0) - FREE_RANK[i]);
  return s;
}

export type LearnDenial = 'max_rank' | 'level' | 'rank_level' | 'prereq' | 'no_points' | 'bad_node';
/**
 * 한 랭크 올릴 수 있는지. null = 가능.
 * 선행: 같은 가지 바로 아래 층 노드가 1랭크 이상(층 0은 없음).
 */
export function learnDenial(ranks: ArrayLike<number>, level: number, node: number): LearnDenial | null {
  const n = SKILL_NODES[node];
  if (!n) return 'bad_node';
  const cur = ranks[node] | 0;
  if (cur >= n.maxRank) return 'max_rank';
  if (level < TIER_LEVELS[n.tier]) return 'level';
  if (n.kind === 'active' && level < activeRankLevel(cur + 1)) return 'rank_level';
  if (n.tier > 0) {
    const below = SKILL_NODES.findIndex((m) => m.branch === n.branch && m.tier === n.tier - 1);
    if (below >= 0 && (ranks[below] | 0) < 1) return 'prereq';
  }
  if (spentPoints(ranks) >= pointsAtLevel(level)) return 'no_points';
  return null;
}
export const LEARN_DENIAL_TEXT: Readonly<Record<LearnDenial, string>> = {
  max_rank: '이미 최고 레벨입니다.',
  level: '영웅 레벨이 부족합니다.',
  rank_level: '다음 스킬 레벨은 영웅 레벨이 더 높아야 합니다.',
  prereq: '아래 단계 기술을 먼저 배워야 합니다.',
  no_points: '스킬 포인트가 부족합니다.',
  bad_node: '알 수 없는 기술입니다.',
};
/** 배우기·초기화·저장 결과 코드(GrowthView.last.code). 거절 사유는 LearnDenial 그대로 쓴다 */
export type GrowthCode = 'ok' | 'duplicate' | 'paused' | 'no_menu' | 'readonly' | 'save_failed' | 'ranks_reset' | LearnDenial;
export const GROWTH_TEXT: Readonly<Record<GrowthCode, string>> = {
  ...LEARN_DENIAL_TEXT,
  ok: '기술을 배웠어요.',
  duplicate: '이미 처리한 요청이에요.',
  paused: '일시정지 중에는 바꿀 수 없어요.',
  no_menu: '마을의 교관 창이나 장비 창에서만 배울 수 있어요.',
  readonly: '이 기기에서는 기술을 바꿀 수 없어요.',
  save_failed: '저장하지 못해 바뀌지 않았어요. 다시 시도해 주세요.',
  ranks_reset: '저장된 기술 기록이 규칙에 맞지 않아 포인트를 돌려받고 초기화했어요.',
};
export const GROWTH_RESET_TEXT = '스킬 포인트를 모두 돌려받았어요.';

/** 저장된 랭크가 규칙을 지키는지(재로드·다른 탭 병합 뒤 검증). 어기면 초기화 대상 */
export function ranksValid(ranks: ArrayLike<number>, level: number): boolean {
  for (let i = 0; i < NODE_COUNT; i++) {
    const r = ranks[i] | 0, n = SKILL_NODES[i];
    if (r < FREE_RANK[i] || r > n.maxRank) return false;
    if (r > FREE_RANK[i] && level < TIER_LEVELS[n.tier]) return false;
    if (n.kind === 'active' && r > 1 && level < activeRankLevel(r)) return false;
    if (r > 0 && n.tier > 0) {
      const below = SKILL_NODES.findIndex((m) => m.branch === n.branch && m.tier === n.tier - 1);
      if (below >= 0 && (ranks[below] | 0) < 1) return false;
    }
  }
  return spentPoints(ranks) <= pointsAtLevel(level);
}
export function freshRanks(out: Uint8Array = new Uint8Array(NODE_COUNT)): Uint8Array {
  for (let i = 0; i < NODE_COUNT; i++) out[i] = FREE_RANK[i];
  return out;
}

// ── 전투 반영값 ───────────────────────────────────
/** SkillBurst 종류(Ev.SkillBurst.a). 랭크 5·캡스톤 추가 효과의 판정 순간 */
export const BurstKind = { DashWake: 1, StormEye: 2, Aftershock: 3, TwinMoon: 4, Bloom: 5, Thunder: 6 } as const;
export type BurstKindId = (typeof BurstKind)[keyof typeof BurstKind];
/** 추가 효과 수치(판정·연출 공통) */
export const BURST = {
  dashWake: { delay: 21, mult: 1.2 },            // 0.35초 뒤, 돌진 경로 같은 폭
  stormEye: { radius: 6, mult: 2.5, pullAdd: 3 }, // 폭풍 마지막 틱
  aftershock: { delay: 24, radius: 6, mult: 0.8 },// 착지 0.4초 뒤
  twinMoon: { delay: 12, mult: 0.7 },             // 두 번째 초승달 0.2초 뒤
  bloom: { radius: 5, mult: 3.0 },
  thunder: { length: 6, width: 1.6, mult: 1.5 },
} as const;
/** 스킬 4 진홍 초승달: 길이·폭(m)과 날아가는 시간 */
export const CRESCENT = { length: 8, width: 2.4, travelTicks: 18 } as const;

export interface GrowthMods {
  /** 스킬 1..SKILL_MAX 피해 배율(인덱스 0..12, 카엘은 0..4만 쓴다) */
  readonly skillMult: Float32Array;
  /** 스킬 1..SKILL_MAX 랭크(인덱스 0..12). 0 = 못 씀. 자동 습득 스킬(영웅별 표, 카엘 SKILL_AUTO_LEARN: 런처 Lv5·초승달 Lv10)은 그 레벨 이상이면 1 */
  readonly skillRank: Uint8Array;
  cooldownMult: number;
  critAdd: number;
  basicMult: number;
  linkAdd: number;
  maxHpMult: number;
  maxMpAdd: number;
  mpRegenMult: number;
  damageTakenMult: number;
  lifesteal: number;
  awakenGainMult: number;
  awakenDamageMult: number;
  /** BurstKind 비트(1 << kind) */
  bursts: number;
  attackAdd: number;
  maxHpAdd: number;
}
export function makeGrowthMods(): GrowthMods {
  return { skillMult: new Float32Array(GROWTH_SKILL_SLOTS).fill(1), skillRank: new Uint8Array(GROWTH_SKILL_SLOTS), cooldownMult: 1, critAdd: 0, basicMult: 1, linkAdd: 0,
    maxHpMult: 1, maxMpAdd: 0, mpRegenMult: 1, damageTakenMult: 1, lifesteal: 0, awakenGainMult: 1, awakenDamageMult: 1, bursts: 0, attackAdd: 0, maxHpAdd: 0 };
}
/** 스킬 배율·랭크 배열 길이(types.SKILL_MAX와 같은 값, 잎 모듈이라 다시 적는다) */
const GROWTH_SKILL_SLOTS = 13;
/**
 * 레벨·랭크 → 전투 반영값. 장착·로드·던전 입장·레벨업·배우기 때만 부른다.
 * hero(c022): 카엘(기본)은 지금 그대로. 다른 영웅은 스킬 트리가 없으므로 무료 3 + 레벨 자동 습득 랭크 1과 레벨 능력치만 준다(ranks 무시)
 */
export function growthMods(out: GrowthMods, level: number, ranks: ArrayLike<number>, hero: SkillHeroId = 'kael', kept = 0): GrowthMods {
  growthModsInner(out, level, ranks, hero);
  // 환생으로 이어 가는 습득 스킬(레인 R): 레벨·트리로 랭크가 없으면 랭크 1(쓸 수 있음)
  if (kept !== 0) for (let i = 0; i < GROWTH_SKILL_SLOTS; i++) if ((kept & (1 << i)) && out.skillRank[i] === 0) out.skillRank[i] = 1;
  return out;
}
function growthModsInner(out: GrowthMods, level: number, ranks: ArrayLike<number>, hero: SkillHeroId): GrowthMods {
  out.skillMult.fill(1); out.skillRank.fill(0);
  out.cooldownMult = 1; out.critAdd = 0; out.basicMult = 1; out.linkAdd = 0; out.maxHpMult = 1; out.maxMpAdd = 0; out.mpRegenMult = 1;
  out.damageTakenMult = 1; out.lifesteal = 0; out.awakenGainMult = 1; out.awakenDamageMult = 1; out.bursts = 0;
  const lv = Math.max(1, Math.min(LEVEL_MAX, Math.floor(level)));
  // 자동 습득(PRD 3A.1, SKILL-TREE 11절): 런처 Lv5·진홍 초승달 Lv10은 영웅 레벨로 랭크 1을 준다. 트리 노드는 아래에서 랭크를 덮어써 강화한다
  for (let i = 0; i < SKILL_AUTO_LEARN.length; i++) if (lv >= SKILL_AUTO_LEARN[i].level) out.skillRank[SKILL_AUTO_LEARN[i].id - 1] = 1;
  out.attackAdd = (lv - 1) * LEVEL_STATS.attack; out.maxHpAdd = (lv - 1) * LEVEL_STATS.maxHp;
  // MP 성장(BALANCE-1028 1절): 레벨당 최대 MP +3, 초당 회복 +2%(Lv1 3.0 → Lv50 5.9 → Lv99 8.9). 트리 마나 노드는 아래에서 더한다
  out.maxMpAdd = (lv - 1) * LEVEL_STATS.maxMp; out.mpRegenMult = 1 + (lv - 1) * LEVEL_STATS.mpRegen;
  for (let i = 0; i < MP_SKILL_STEPS.length; i++) if (lv >= MP_SKILL_STEPS[i].level) { out.maxMpAdd += MP_SKILL_STEPS[i].maxMp; out.mpRegenMult += MP_SKILL_STEPS[i].mpRegen; }
  if (hero !== 'kael') {
    out.skillRank.fill(0);
    const m = skillsUnlockedAt(lv, hero);
    for (let i = 0; i < GROWTH_SKILL_SLOTS; i++) if (m & (1 << i)) out.skillRank[i] = 1;
    return out;
  }
  for (let i = 0; i < NODE_COUNT; i++) {
    const n = SKILL_NODES[i], r = ranks[i] | 0;
    if (r <= 0) continue;
    switch (n.effect) {
      case 'skillDamage':
        out.skillRank[n.skillId - 1] = r;
        out.skillMult[n.skillId - 1] = 1 + n.per * (r - 1);
        if (r >= 5) out.bursts |= 1 << (n.skillId === 1 ? BurstKind.DashWake : n.skillId === 2 ? BurstKind.StormEye : n.skillId === 3 ? BurstKind.Aftershock : BurstKind.TwinMoon);
        break;
      case 'critChance': out.critAdd += n.per * r; break;
      case 'skillLifesteal': out.lifesteal += n.per * r; break;
      case 'basicDamage': out.basicMult += n.per * r; break;
      case 'cooldown': out.cooldownMult -= n.per * r; break;
      case 'linkDamage': out.linkAdd += n.per * r; break;
      case 'maxHp': out.maxHpMult += n.per * r; break;
      case 'mana': out.mpRegenMult += n.per * r; out.maxMpAdd += 10 * r; break;
      case 'damageTaken': out.damageTakenMult -= n.per * r; break;
      case 'bloom': out.bursts |= 1 << BurstKind.Bloom; break;
      case 'thunder': out.bursts |= 1 << BurstKind.Thunder; break;
      case 'resonance': out.awakenGainMult += n.per; out.awakenDamageMult += 0.25; break;
    }
  }
  return out;
}

/** XpGain.b 원인: 처치, 보스 처치, 지역 클리어 */
export const XpCause = { Kill: 0, Boss: 1, Clear: 2 } as const;
